import { blobMediaUrl } from "@/lib/blob-url"
import type { Translator } from "@/lib/intl-types"
import type { BiomarkerLandingConfig } from "@/components/ui/biomarker-landing"

/**
 * Lp(a) single-biomarker landing page content (AHD-3795).
 *
 * Copy is sourced from the `go` i18n namespace (keys prefixed `lpa*`). `en.json`
 * is the source of truth; the other locales are EN stubs pending real
 * translation + review-cockpit sign-off (see /translation-workflow). A few
 * fields stay as literals: image alt text, the rating, units, and the FAQ
 * fallback (the live FAQ comes from the CMS) + the unused personas block
 * (testimonials replaced it).
 *
 * NEEDS CLINICAL SIGN-OFF before launch: the reference-range bands and units
 * (nmol/L) and every prevalence / risk claim (HWG review).
 */
export function lpaConfig(
  t: Translator<"go">,
  whatWeDetectHref: string,
  advancedBloodTestHref: string,
  priceFrom?: string
): BiomarkerLandingConfig {
  return {
    slug: "lpa",
    advancedBloodTestHref,

    hero: {
      eyebrow: t("lpaHeroEyebrow"),
      badge: t("lpaHeroBadge"),
      ctaNote: t("lpaHeroCtaNote"),
      ctaNoteHref: advancedBloodTestHref,
      heading: t("lpaHeroHeading"),
      body: t("lpaHeroBody"),
      primaryCta: t("lpaHeroPrimaryCta"),
      secondaryCta: t("lpaHeroSecondaryCta"),
      secondaryHref: "#what-it-is",
      // Dropped an unsourced "5.0 · 34 reviews" literal here — unrendered, but
      // one wire-up away from a fabricated review count (matches PSA/cortisol).
      priceFrom,
      image: {
        src: blobMediaUrl("/images/marketing/lpa-hero-train.jpeg"),
        alt: "Woman looking out of a train window in warm morning light",
        position: "center",
      },
      // Floating result cards tell the core story at a glance: cholesterol
      // looks fine, but Lp(a) — the inherited marker — is elevated.
      floatingCards: [
        {
          name: t("lpaHeroCardCholesterolName"),
          value: "2.8 mmol/L",
          status: t("lpaHeroCardInRange"),
          tone: "good",
        },
        {
          name: "Lp(a)",
          value: "94 nmol/L",
          status: t("lpaHeroCardElevated"),
          tone: "bad",
        },
      ],
    },

    // Test-detail info card (mirrors the /tests/<slug> quick-facts block).
    // NEEDS CLINICAL SIGN-OFF on the prep window and "for whom" claims.
    detailCard: {
      heading: t("lpaDetailHeading"),
      benefits: [
        t("lpaDetailBenefit1"),
        t("lpaDetailBenefit2"),
        t("lpaDetailBenefit3"),
        t("lpaDetailBenefit4"),
      ],
      primaryCta: t("lpaHeroPrimaryCta"),
      secondaryCta: {
        label: t("lpaDetailSecondaryCta"),
        href: advancedBloodTestHref,
      },
      rows: [
        {
          icon: "healthAreas",
          label: t("lpaDetailRow1Label"),
          value: t("lpaDetailRow1Value"),
        },
        {
          icon: "time",
          label: t("lpaDetailRow2Label"),
          value: t("lpaDetailRow2Value"),
        },
        {
          icon: "radiation",
          label: t("lpaDetailRow3Label"),
          value: t("lpaDetailRow3Value"),
        },
        {
          icon: "contrast",
          label: t("lpaDetailRow4Label"),
          value: t("lpaDetailRow4Value"),
        },
        {
          icon: "preparation",
          label: t("lpaDetailRow5Label"),
          value: t("lpaDetailRow5Value"),
        },
        {
          icon: "forWhom",
          label: t("lpaDetailRow6Label"),
          value: t("lpaDetailRow6Value"),
        },
      ],
    },

    facts: {
      eyebrow: t("lpaFactsEyebrow"),
      heading: t("lpaFactsHeading"),
      headlineStat: {
        value: t("lpaFactsStatValue"),
        caption: t("lpaFactsStatCaption"),
      },
      items: [
        { title: t("lpaFact1Title"), body: t("lpaFact1Body"), icon: "dna" },
        {
          title: t("lpaFact2Title"),
          body: t("lpaFact2Body"),
          icon: "eyeSlash",
        },
        { title: t("lpaFact3Title"), body: t("lpaFact3Body"), icon: "check" },
        { title: t("lpaFact4Title"), body: t("lpaFact4Body"), icon: "heart" },
      ],
    },

    gap: {
      eyebrow: t("lpaGapEyebrow"),
      heading: t("lpaGapHeading"),
      body: t("lpaGapBody"),
      image: {
        src: blobMediaUrl("/images/homepage/age-40s.jpg"),
        alt: "Healthy-looking person in their forties",
        position: "center",
      },
      // Auto-scrolling biomarker marquee (reused from /report). A standard panel
      // reads clean across the board while Lp(a) is never measured (greyed).
      // Values/units are illustrative literals; labels + statuses are i18n.
      marquee: [
        {
          label: t("lpaMarker1"),
          value: "4.8",
          unit: "mmol/L",
          status: t("lpaStatusNormal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 30, color: "#ed9062" },
            { h: 45, color: "#408c49", active: true },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 45,
          ghosts: [50, 42],
        },
        {
          label: t("lpaMarker2"),
          value: "1.6",
          unit: "mmol/L",
          status: t("lpaStatusOptimal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 50, color: "#408c49", active: true },
            { h: 25, color: "#c8b59d" },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 22,
          ghosts: [28, 25],
        },
        {
          label: t("lpaMarker3"),
          value: "1.8",
          unit: "mmol/L",
          status: t("lpaStatusNormal"),
          statusBg: "bg-deep-green-500/10",
          statusText: "text-deep-green-500",
          segments: [
            { h: 30, color: "#ed9062" },
            { h: 45, color: "#408c49", active: true },
            { h: 25, color: "#ed9062" },
          ],
          dotY: 48,
          ghosts: [52, 46],
        },
        {
          label: t("lpaMarker4"),
          value: "—",
          unit: "nmol/L",
          status: t("lpaStatusNotMeasured"),
          statusBg: "",
          statusText: "",
          segments: [
            { h: 33, color: "#e5e5e5" },
            { h: 34, color: "#e5e5e5" },
            { h: 33, color: "#e5e5e5" },
          ],
          dotY: 50,
          ghosts: [],
          muted: true,
        },
      ],
    },

    explorer: {
      eyebrow: t("lpaExplorerEyebrow"),
      heading: t("lpaExplorerHeading"),
      caption: t("lpaExplorerCaption"),
      unit: "nmol/L",
      // Capped at 180 (not 250) so the "Elevated" band is a slim top segment
      // rather than half the track — desirable stays the widest, default zone.
      sliderMax: 180,
      defaultValue: 44,
      bands: [
        {
          label: t("lpaBand1Label"),
          tone: "good",
          from: 0,
          to: 75,
          status: t("lpaBand1Status"),
          level: 1,
          meaning: t("lpaBand1Meaning"),
        },
        {
          label: t("lpaBand2Label"),
          tone: "warn",
          from: 75,
          to: 125,
          status: t("lpaBand2Status"),
          level: 2,
          meaning: t("lpaBand2Meaning"),
        },
        {
          label: t("lpaBand3Label"),
          tone: "bad",
          from: 125,
          to: 180,
          status: t("lpaBand3Status"),
          level: 3,
          meaning: t("lpaBand3Meaning"),
        },
      ],
      factorsLabel: t("lpaExplorerFactorsLabel"),
      factors: [
        t("lpaFactor1"),
        t("lpaFactor2"),
        t("lpaFactor3"),
        t("lpaFactor4"),
      ],
    },

    risks: {
      eyebrow: t("lpaRisksEyebrow"),
      heading: t("lpaRisksHeading"),
      body: t("lpaRisksBody"),
      seeAllLabel: t("lpaRisksSeeAll"),
      seeAllHref: whatWeDetectHref,
      items: [
        { title: t("lpaRisk1Title"), body: t("lpaRisk1Body"), icon: "heart" },
        { title: t("lpaRisk2Title"), body: t("lpaRisk2Body"), icon: "brain" },
        { title: t("lpaRisk3Title"), body: t("lpaRisk3Body"), icon: "valve" },
        { title: t("lpaRisk4Title"), body: t("lpaRisk4Body"), icon: "warning" },
      ],
    },

    selfCheck: {
      eyebrow: t("lpaSelfCheckEyebrow"),
      heading: t("lpaSelfCheckHeading"),
      intro: t("lpaSelfCheckIntro"),
      questions: [
        { id: "family", label: t("lpaSelfCheckQ1") },
        { id: "event", label: t("lpaSelfCheckQ2") },
        { id: "cholesterol", label: t("lpaSelfCheckQ3") },
        { id: "prevention", label: t("lpaSelfCheckQ4") },
      ],
      results: [
        {
          minChecks: 0,
          tone: "good",
          title: t("lpaSelfCheckResult1Title"),
          body: t("lpaSelfCheckResult1Body"),
        },
        {
          minChecks: 1,
          tone: "warn",
          title: t("lpaSelfCheckResult2Title"),
          body: t("lpaSelfCheckResult2Body"),
        },
        {
          minChecks: 2,
          tone: "bad",
          title: t("lpaSelfCheckResult3Title"),
          body: t("lpaSelfCheckResult3Body"),
        },
      ],
      primaryCta: t("lpaSelfCheckCta"),
    },

    action: {
      eyebrow: t("lpaActionEyebrow"),
      heading: t("lpaActionHeading"),
      body: t("lpaActionBody"),
      items: [
        {
          title: t("lpaAction1Title"),
          body: t("lpaAction1Body"),
          icon: "actionPlan",
        },
        {
          title: t("lpaAction2Title"),
          body: t("lpaAction2Body"),
          icon: "stethoscope",
        },
        {
          title: t("lpaAction3Title"),
          body: t("lpaAction3Body"),
          icon: "users",
        },
        {
          title: t("lpaAction4Title"),
          body: t("lpaAction4Body"),
          icon: "checklist",
        },
      ],
      image: {
        src: blobMediaUrl("/images/marketing/lpa-action-hiker.jpeg"),
        alt: "Hiker on a mountain ridge at sunset looking toward distant peaks",
        position: "center",
      },
    },

    // Unused (the live TestimonialsSection replaces this block) — kept English.
    personas: {
      eyebrow: "Real-world reasons",
      heading: "People who decided to know their number",
      note: "Illustrative profiles, not testimonials.",
      items: [
        {
          name: "David, 45",
          scenario:
            "His father had a heart attack at 52. David's cholesterol was normal, his Lp(a) was not.",
          photo: {
            src: blobMediaUrl("/images/report/person-david.jpg"),
            alt: "Portrait of a man in his forties",
          },
        },
        {
          name: "Eva, 38",
          scenario:
            "She wanted the full picture of her heart risk before starting a family.",
          photo: {
            src: blobMediaUrl("/images/report/person-eva.jpg"),
            alt: "Portrait of a woman in her late thirties",
          },
        },
        {
          name: "Lena, 48",
          scenario:
            "Heart disease runs in her family, even though everyone's cholesterol looks fine.",
          photo: {
            src: blobMediaUrl("/images/report/person-lena.jpg"),
            alt: "Portrait of a woman in her late forties",
          },
        },
      ],
    },

    biomarkerPanel: {
      eyebrow: t("lpaPanelEyebrow"),
      heading: t("lpaPanelHeading"),
      subheading: t("lpaPanelSubheading"),
      ctaLabel: t("lpaPanelCta"),
    },

    steps: {
      eyebrow: t("lpaStepsEyebrow"),
      heading: t("lpaStepsHeading"),
      body: t("lpaStepsBody"),
      stepWord: t("lpaStepsStepWord"),
      sampleReportCta: t("lpaStepsSampleReport"),
      locationsCta: t("lpaStepsLocationsCta"),
      items: [
        {
          title: t("lpaStep1Title"),
          body: t("lpaStep1Body"),
          image: {
            src: blobMediaUrl("/images/marketing/lpa-step1-booked.jpeg"),
            alt: "Person booking their blood test on a laptop at home",
          },
        },
        {
          title: t("lpaStep2Title"),
          body: t("lpaStep2Body"),
          image: {
            src: blobMediaUrl("/images/marketing/lpa-step2-blood-draw.jpeg"),
            alt: "Clinician taking a blood sample from a smiling patient",
            // Landscape (not 4:3) — bias the crop toward the faces and the draw.
            position: "50% 40%",
          },
        },
        {
          title: t("lpaStep3Title"),
          body: t("lpaStep3Body"),
          image: {
            src: blobMediaUrl("/images/marketing/lpa-step3-report.jpeg"),
            alt: "Tablet showing a health report on a kitchen counter",
          },
        },
      ],
    },

    faq: {
      heading: t("lpaFaqHeading"),
      // Fallback only — the live FAQ comes from the CMS (Advanced blood test set).
      items: [
        {
          q: "Is Lp(a) on my normal blood test?",
          a: "Almost never. Standard lipid panels measure LDL, HDL and total cholesterol, not Lp(a). It needs to be requested specifically, which is why most people have never had it measured.",
        },
        {
          q: "Can I lower my Lp(a)?",
          a: "Diet and exercise have little effect because Lp(a) is genetic. Knowing your level matters because it changes how aggressively you and your doctor manage the risk factors you can move, and specific Lp(a)-lowering therapies are emerging.",
        },
        {
          q: "Do I need to retest it?",
          a: "Generally no. Levels stay stable across your life, so a single measurement gives you a number you can keep using.",
        },
        {
          q: "What level is considered high?",
          a: "We report in nmol/L against established thresholds. As a guide, below 75 is desirable, 75 to 125 is borderline, and above 125 signals higher cardiovascular risk. Your report explains your band in plain language.",
        },
        {
          q: "Is it covered by insurance?",
          a: "Coverage varies by insurer and plan. Your report and our team can help you understand what applies in your case.",
        },
      ],
    },

    closing: {
      eyebrow: t("lpaClosingEyebrow"),
      heading: t("lpaClosingHeading"),
      body: t("lpaClosingBody"),
      primaryCta: t("lpaClosingPrimaryCta"),
      secondaryCta: t("lpaClosingSecondaryCta"),
      secondaryHref: "#what-it-is",
      image: {
        src: blobMediaUrl("/images/marketing/lpa-closing-hiker-trail.jpg"),
        alt: "Hiker on an alpine trail with snow-capped Swiss mountains",
        position: "center",
      },
    },
  }
}
